import { Component, ViewChild, inject } from '@angular/core';
import { GridApi, GridReadyEvent, ProcessDataFromClipboardParams, RowSelectedEvent, SelectionChangedEvent } from 'ag-grid-community';
import { firstValueFrom, take } from 'rxjs';
import Swal from 'sweetalert2';
import { GridComponent } from 'src/app/shared/components/grid/grid.component';
import { GridTransaction } from 'src/app/shared/components/grid/grid-transaction';
import { EfateCakeDataSet, EfateCakeExperimentData } from 'src/app/shared/models/echo/efate-cake-data-set';
import { CakeTopologyBidirectionalDirection, EfCakeTopologyGroupDefinition, EfCakeTopologyUiState } from 'src/app/shared/models/ef-cake-topology.model';
import { EfDataExperimentRow, EfDataPercentColumn, EfDataSaveTransaction, EfDataSetRow } from 'src/app/shared/models/ef-data.model';
import { EfateCakeDatasetApiService } from 'src/app/shared/services/efate-cake/efate-cake-dataset.api.service';
import { EFateConstants } from 'src/app/shared/utils/efate-constants';
import { EfCakeTopologySharedService } from '../../shared/services/ef-cake-topology-shared.service';
import { EfCakeTopologyStateUtils } from '../../shared/utils/ef-cake-topology-state.utils';
import { EFDataColDef } from './ef-data-coldef';

@Component({
  selector: 'app-ef-data',
  templateUrl: './ef-data.component.html',
  styleUrls: ['./ef-data.component.css']
})
export class EFDataComponent {
  private readonly efateCakeDatasetApiService = inject(EfateCakeDatasetApiService);
  private readonly efCakeTopologySharedService = inject(EfCakeTopologySharedService);
  private readonly efDataColDef = inject(EFDataColDef);

  @ViewChild('grid') grid?: GridComponent;

  columnDefs: any[] = [];
  rowData: EfDataSetRow[] = [];
  showDetail: boolean = false;
  expandedRow?: EfDataSetRow;

  dataGroupOptions: EfCakeTopologyGroupDefinition[] = [];
  topologyNodeNames: Record<string, string> = { ...EFateConstants.DEFAULT_NODE_NAMES };
  selectedGroupColor: string = EFateConstants.GROUP_COLORS[0];
  selectedGroupActiveEdges = new Set<string>();
  selectedGroupActiveStatusNodes = new Set<string>();
  selectedGroupBidirectionalEdges: Record<string, CakeTopologyBidirectionalDirection> = { ...EFateConstants.DEFAULT_BIDIRECTIONAL_EDGE_DIRECTIONS };

  percentColumns: EfDataPercentColumn[] = [];
  experimentColumnDefs: any[] = [];
  experimentRowData: EfDataExperimentRow[] = [];
  experimentDefaultColDef = {
    resizable: true,
    suppressMovable: true,
  };
  readonly experimentRowHeight = EFateConstants.DATA_ROW_HEIGHT;
  readonly experimentHeaderHeight = EFateConstants.DATA_HEADER_HEIGHT;
  readonly experimentGridHeight = `${EFateConstants.DATA_HEADER_HEIGHT + EFateConstants.DATA_VISIBLE_ROWS * EFateConstants.DATA_ROW_HEIGHT + 2}px`;

  private efateSetupPk?: number;
  private topologyState?: EfCakeTopologyUiState;
  private defaultGroupKey: string = EFateConstants.DEFAULT_GROUP_KEY;
  private dataSets: EfDataSetRow[] = [];
  private deletedDataSets: EfDataSetRow[] = [];
  private experimentGridApi?: GridApi;
  private pendingSave: Promise<boolean> = Promise.resolve(true);

  constructor() {
    this.columnDefs = this.efDataColDef.getDataSetColumnsDefinition(false, this);
  }

  async loadReadOnlyCakeTopology(efateSetupPk?: number): Promise<void> {
    this.efateSetupPk = efateSetupPk;
    this.topologyState = await this.efCakeTopologySharedService.getCakeTopologyStateBySetupPk(efateSetupPk);
    this.topologyNodeNames = { ...EFateConstants.DEFAULT_NODE_NAMES, ...(this.topologyState?.nodeNames ?? {}) };
    this.dataGroupOptions = this.buildDataGroupOptions();
    this.defaultGroupKey = this.resolveDefaultGroupKey();
    await this.loadDataSets();
  }

  async saveDataSets(efateSetupPk?: number): Promise<boolean> {
    const save = this.pendingSave
      .catch(() => false)
      .then(() => this.persistDataSets(efateSetupPk));

    this.pendingSave = save;

    return save;
  }

  private async persistDataSets(efateSetupPk?: number): Promise<boolean> {
    if (!this.efateSetupPk || this.efateSetupPk <= 0) {
      return true;
    }

    if (efateSetupPk && efateSetupPk !== this.efateSetupPk) {
      return false;
    }

    this.syncExpandedExperimentData();
    const transactions = this.buildSaveTransactions();

    if (transactions.length === 0) {
      return true;
    }

    try {
      await firstValueFrom(this.efateCakeDatasetApiService.saveEfateCakeDataSet(transactions).pipe(take(1)));
      if (this.grid) {
        this.grid.transactionList = [];
      }
      this.deletedDataSets = [];
      await this.reconcileSavedDataSets();
      return true;
    } catch (error) {
      console.warn('Error saving CAKE data sets', error);
      return false;
    }
  }

  onAdd(newItems: any): void {
    const newRow: EfDataSetRow = Array.isArray(newItems) ? newItems[0] : newItems;
    if (!newRow) {
      return;
    }

    newRow.efateSetupPk = this.efateSetupPk ?? 0;
    newRow.studyType = newRow.studyType ?? '';
    newRow.dataSetName = newRow.dataSetName ?? '';
    newRow.dataGroupKey = newRow.dataGroupKey || this.defaultGroupKey;
    newRow.useInProject = newRow.useInProject ?? true;
    newRow.experimentData = newRow.experimentData ?? [];

    if (!this.dataSets.includes(newRow)) {
      this.dataSets.unshift(newRow);
    }

    this.refreshRunSelection();
  }

  onDelete(transactions: GridTransaction[]): void {
    const transactionList = Array.isArray(transactions) ? transactions : [];

    transactionList.forEach((transaction: GridTransaction) => {
      const row: EfDataSetRow = typeof transaction?.getRow === 'function' ? transaction.getRow() : (transaction as any)?.row;
      if (!row) {
        return;
      }

      const index = this.dataSets.indexOf(row);
      if (index > -1) {
        this.dataSets.splice(index, 1);
      }

      if ((row.efateCakeDataSetPk ?? 0) > 0 && !this.deletedDataSets.includes(row)) {
        this.deletedDataSets.push(row);
      }
    });
  }

  onDataSetFieldChanged(): void {
    this.grid?.gridApi?.refreshCells({ force: true, columns: [EFDataColDef.EXPAND_COLUMN_ID] });
  }

  async onRunSelectionChanged(event: RowSelectedEvent): Promise<void> {
    this.applyRunSelection(event.node);
    await this.saveDataSets();
  }

  async onRunSelectionAllChanged(event: SelectionChangedEvent): Promise<void> {
    if (event.source !== 'uiSelectAll' || !this.grid?.gridApi) {
      return;
    }

    this.grid.gridApi.forEachNode((node: any) => this.applyRunSelection(node));
    await this.saveDataSets();
  }

  async onButtonExpandClick(params: any): Promise<void> {
    const row: EfDataSetRow = params?.data;
    if (!row) {
      return;
    }

    if (row.isExpanded) {
      this.expandDataSet(row);
      return;
    }

    await this.collapseExpandedRow();
  }

  async onDataGroupChange(groupKey: string): Promise<void> {
    const row = this.expandedRow;
    if (!row || !groupKey || groupKey === row.dataGroupKey) {
      return;
    }

    const previousGroupKey = row.dataGroupKey;
    const previousColumns = this.percentColumns;
    const lostColumns = this.getColumnsLostOnGroupChange(groupKey);

    row.dataGroupKey = groupKey;

    if (lostColumns.length > 0 && !(await this.confirmGroupChange(lostColumns))) {
      row.dataGroupKey = previousGroupKey;
      return;
    }

    row.modified = true;
    this.applySelectedGroup(groupKey);
    this.remapPercentValues(previousColumns);
    this.clearHiddenPercentValues();

    await this.saveDataSets();
  }

  onExperimentGridReady({ api }: GridReadyEvent): void {
    this.experimentGridApi = api;
  }

  onExperimentCellValueChanged(): void {
    if (this.expandedRow) {
      this.expandedRow.modified = true;
    }

    this.ensureTrailingBlankRow();
  }

  processDataFromClipboard = (params: ProcessDataFromClipboardParams): string[][] | null => {
    const data = (params.data ?? []).map((row: string[]) => [...row]);

    while (data.length > 0 && data[data.length - 1].every((cell: string) => cell === null || cell === undefined || `${cell}`.trim() === '')) {
      data.pop();
    }

    if (data.length === 0) {
      return null;
    }

    const startRowIndex = params.api.getFocusedCell()?.rowIndex ?? 0;
    const missingRows = startRowIndex + data.length - params.api.getDisplayedRowCount();

    for (let rowIndex = 0; rowIndex < missingRows; rowIndex++) {
      this.appendBlankExperimentRow();
    }

    return data;
  };

  showDialog(_element: any, action: string, params?: any, _nodeId?: any): void {
    if (action !== 'delete' || !params?.data) {
      return;
    }

    if (params.isDataSetGrid) {
      params.data.deleted = true;
      this.grid?.deleteRowFromGrid(params);
      return;
    }

    this.removeExperimentRow(params.data as EfDataExperimentRow);
  }

  private async loadDataSets(): Promise<void> {
    this.resetExpandedState();
    this.dataSets = [];
    this.deletedDataSets = [];
    this.rowData = this.dataSets;
    this.refreshColumnDefs();

    if (!this.efateSetupPk || this.efateSetupPk <= 0) {
      return;
    }

    try {
      const dataSets = await firstValueFrom(this.efateCakeDatasetApiService.getEfateCakeDataSetsBySetupPk(this.efateSetupPk).pipe(take(1)));
      this.dataSets = (dataSets ?? []).map((dataSet: EfateCakeDataSet) => this.mapDataSetToRow(dataSet));
    } catch (error) {
      console.warn('Error loading CAKE data sets', error);
      this.dataSets = [];
    }

    this.rowData = this.dataSets;
    this.refreshRunSelection();
  }

  private mapDataSetToRow(dataSet: EfateCakeDataSet): EfDataSetRow {
    return {
      efateCakeDataSetPk: dataSet.efateCakeDataSetPk,
      efateSetupPk: dataSet.efateSetupPk,
      studyType: dataSet.studyType ?? '',
      dataSetName: dataSet.dataSetName ?? '',
      dataGroupKey: dataSet.dataGroupKey || this.defaultGroupKey,
      useInProject: dataSet.useInProject !== false,
      experimentData: (dataSet.experimentData ?? []).map((experimentData: EfateCakeExperimentData, index: number) => ({
        ...experimentData,
        dataGroupKey: dataSet.dataGroupKey || this.defaultGroupKey,
        sortOrder: index,
      })),
      deletable: true,
      sortable: true,
    };
  }

  private buildSaveTransactions(): EfDataSaveTransaction[] {
    const transactions: EfDataSaveTransaction[] = [];

    this.dataSets.forEach((row: EfDataSetRow) => {
      if (!this.isSavableDataSet(row)) {
        return;
      }

      const isNewDataSet = (row.efateCakeDataSetPk ?? 0) <= 0;
      transactions.push({
        transactionType: isNewDataSet ? GridTransaction.insert : GridTransaction.update,
        row: this.mapRowToDataSet(row),
      });
    });

    this.deletedDataSets.forEach((row: EfDataSetRow) => {
      transactions.push({
        transactionType: GridTransaction.delete,
        row: this.mapRowToDataSet(row),
      });
    });

    return transactions;
  }

  private isSavableDataSet(row: EfDataSetRow): boolean {
    return (row.dataSetName ?? '').trim() !== '' || (row.studyType ?? '').trim() !== '';
  }

  private mapRowToDataSet(row: EfDataSetRow): EfateCakeDataSet {
    const dataSetPk = row.efateCakeDataSetPk ?? 0;

    return {
      efateCakeDataSetPk: dataSetPk,
      efateSetupPk: this.efateSetupPk ?? row.efateSetupPk,
      studyType: row.studyType ?? '',
      dataSetName: row.dataSetName ?? '',
      dataGroupKey: row.dataGroupKey || this.defaultGroupKey,
      useInProject: row.useInProject !== false,
      experimentData: (row.experimentData ?? [])
        .filter((experimentRow: EfDataExperimentRow) => !this.isBlankExperimentRow(experimentRow))
        .map((experimentRow: EfDataExperimentRow) => ({
          efateCakeExperimentDataPk: experimentRow.efateCakeExperimentDataPk ?? 0,
          efateCakeDataSetPk: dataSetPk,
          dataGroupKey: row.dataGroupKey || this.defaultGroupKey,
          sortOrder: experimentRow.sortOrder ?? 0,
          timeDays: experimentRow.timeDays ?? null,
          parentPercent: experimentRow.parentPercent ?? null,
          a1Percent: experimentRow.a1Percent ?? null,
          a2Percent: experimentRow.a2Percent ?? null,
          a3Percent: experimentRow.a3Percent ?? null,
          b1Percent: experimentRow.b1Percent ?? null,
          b2Percent: experimentRow.b2Percent ?? null,
          c1Percent: experimentRow.c1Percent ?? null,
        })),
    };
  }

  private expandDataSet(row: EfDataSetRow): void {
    this.expandedRow = row;
    this.showDetail = true;
    this.rowData = [row];
    this.experimentGridApi = undefined;
    row.dataGroupKey = this.resolveRowGroupKey(row);
    this.applySelectedGroup(row.dataGroupKey);
    this.experimentRowData = this.getExperimentRows(row);
    this.clearHiddenPercentValues();
    this.refreshColumnDefs();
    this.refreshRunSelection();
  }

  private async collapseExpandedRow(): Promise<void> {
    this.syncExpandedExperimentData();
    this.resetExpandedState();
    this.rowData = this.dataSets;
    this.refreshColumnDefs();
    this.refreshRunSelection();
    await this.saveDataSets();
  }

  private async reconcileSavedDataSets(): Promise<void> {
    if (!this.efateSetupPk || this.efateSetupPk <= 0) {
      return;
    }

    let savedDataSets: EfateCakeDataSet[] = [];

    try {
      savedDataSets = await firstValueFrom(this.efateCakeDatasetApiService.getEfateCakeDataSetsBySetupPk(this.efateSetupPk).pipe(take(1))) ?? [];
    } catch (error) {
      console.warn('Error reading back CAKE data sets', error);
      return;
    }

    const unmatched = [...savedDataSets].sort((first, second) => (first.efateCakeDataSetPk ?? 0) - (second.efateCakeDataSetPk ?? 0));

    this.dataSets
      .filter((row: EfDataSetRow) => (row.efateCakeDataSetPk ?? 0) > 0)
      .forEach((row: EfDataSetRow) => this.matchSavedDataSet(row, unmatched,
        (savedDataSet: EfateCakeDataSet) => savedDataSet.efateCakeDataSetPk === row.efateCakeDataSetPk));

    this.dataSets
      .filter((row: EfDataSetRow) => (row.efateCakeDataSetPk ?? 0) <= 0 && this.isSavableDataSet(row))
      .forEach((row: EfDataSetRow) => this.matchSavedDataSet(row, unmatched,
        (savedDataSet: EfateCakeDataSet) => (savedDataSet.dataSetName ?? '').trim().toLowerCase() === (row.dataSetName ?? '').trim().toLowerCase()));
  }

  private matchSavedDataSet(row: EfDataSetRow, unmatched: EfateCakeDataSet[], predicate: (savedDataSet: EfateCakeDataSet) => boolean): void {
    const index = unmatched.findIndex(predicate);
    if (index < 0) {
      return;
    }

    this.applySavedKeys(row, unmatched.splice(index, 1)[0]);
  }

  private applySavedKeys(row: EfDataSetRow, savedDataSet: EfateCakeDataSet): void {
    row.efateCakeDataSetPk = savedDataSet.efateCakeDataSetPk;
    row.newRow = false;
    row.modified = false;

    const savedRowsBySortOrder = new Map<number, EfateCakeExperimentData>();
    (savedDataSet.experimentData ?? []).forEach((savedRow: EfateCakeExperimentData) => {
      savedRowsBySortOrder.set(savedRow.sortOrder ?? 0, savedRow);
    });

    (row.experimentData ?? []).forEach((experimentRow: EfDataExperimentRow) => {
      if (this.isBlankExperimentRow(experimentRow)) {
        return;
      }

      const savedRow = savedRowsBySortOrder.get(experimentRow.sortOrder ?? 0);
      if (!savedRow) {
        return;
      }

      experimentRow.efateCakeExperimentDataPk = savedRow.efateCakeExperimentDataPk;
      experimentRow.efateCakeDataSetPk = savedRow.efateCakeDataSetPk;
    });
  }

  private applyRunSelection(node: any): void {
    const row: EfDataSetRow = node?.data;
    if (!row) {
      return;
    }

    row.useInProject = node.isSelected();
    row.modified = true;
  }

  private refreshRunSelection(): void {
    setTimeout(() => {
      if (this.grid?.gridApi) {
        this.grid.refreshSelectedValues('useInProject');
      }
    }, 75);
  }

  private resetExpandedState(): void {
    if (this.expandedRow) {
      this.expandedRow.isExpanded = false;
    }

    this.expandedRow = undefined;
    this.showDetail = false;
    this.experimentGridApi = undefined;
    this.experimentRowData = [];
    this.experimentColumnDefs = [];
    this.percentColumns = [];
  }

  private syncExpandedExperimentData(): void {
    const row = this.expandedRow;
    if (!row) {
      return;
    }

    let sortOrder = 0;

    (row.experimentData ?? []).forEach((experimentRow: EfDataExperimentRow) => {
      if (this.isBlankExperimentRow(experimentRow)) {
        return;
      }

      experimentRow.dataGroupKey = row.dataGroupKey;
      experimentRow.sortOrder = sortOrder;
      sortOrder++;
    });
  }

  private refreshColumnDefs(): void {
    this.columnDefs = this.efDataColDef.getDataSetColumnsDefinition(this.showDetail, this);
  }

  private applySelectedGroup(groupKey: string): void {
    const groupTopology = this.getGroupTopology(groupKey);

    this.selectedGroupActiveEdges = groupTopology.activeEdges;
    this.selectedGroupActiveStatusNodes = groupTopology.activeStatusNodes;
    this.selectedGroupBidirectionalEdges = groupTopology.bidirectionalEdges;
    this.selectedGroupColor = this.getGroupColor(groupKey);
    this.percentColumns = this.buildPercentColumns(groupKey);
    this.experimentColumnDefs = this.efDataColDef.getExperimentColumnsDefinition(this.percentColumns, this);
  }

  private getGroupTopology(groupKey: string): {
    activeEdges: Set<string>;
    activeStatusNodes: Set<string>;
    bidirectionalEdges: Record<string, CakeTopologyBidirectionalDirection>;
  } {
    const snapshot = this.topologyState?.topologyByGroup?.[groupKey];

    return {
      activeEdges: new Set<string>(snapshot?.activeEdges ?? []),
      activeStatusNodes: new Set<string>(snapshot?.activeStatusNodes ?? EFateConstants.DEFAULT_ACTIVE_STATUS_NODES),
      bidirectionalEdges: {
        ...EFateConstants.DEFAULT_BIDIRECTIONAL_EDGE_DIRECTIONS,
        ...(snapshot?.bidirectionalEdges ?? {}),
      },
    };
  }

  private buildPercentColumns(groupKey: string): EfDataPercentColumn[] {
    const { activeEdges, bidirectionalEdges } = this.getGroupTopology(groupKey);

    return EFateConstants.TOPOLOGY_NODE_KEYS
      .filter((nodeKey: string) => !!EFateConstants.NODE_KEY_TO_PERCENT_FIELD[nodeKey]
        && EfCakeTopologyStateUtils.isNodeActivated(nodeKey, activeEdges, bidirectionalEdges))
      .map((nodeKey: string) => ({
        nodeKey,
        field: EFateConstants.NODE_KEY_TO_PERCENT_FIELD[nodeKey],
        headerName: `${this.topologyNodeNames[nodeKey] || nodeKey} (%)`,
      }));
  }

  private getColumnsLostOnGroupChange(groupKey: string): EfDataPercentColumn[] {
    const nextColumnCount = this.buildPercentColumns(groupKey).length;

    return this.percentColumns
      .slice(nextColumnCount)
      .filter((percentColumn: EfDataPercentColumn) => this.experimentRowData
        .some((experimentRow: EfDataExperimentRow) => !this.isBlankValue(experimentRow[percentColumn.field])));
  }

  private remapPercentValues(previousColumns: EfDataPercentColumn[]): void {
    this.experimentRowData.forEach((experimentRow: EfDataExperimentRow) => {
      const values = previousColumns.map((percentColumn: EfDataPercentColumn) => experimentRow[percentColumn.field]);

      this.percentColumns.forEach((percentColumn: EfDataPercentColumn, index: number) => {
        experimentRow[percentColumn.field] = values[index] ?? null;
      });
    });

    this.experimentGridApi?.refreshCells({ force: true });
  }

  private async confirmGroupChange(lostColumns: EfDataPercentColumn[]): Promise<boolean> {
    const columnNames = lostColumns.map((percentColumn: EfDataPercentColumn) => percentColumn.headerName).join(', ');

    const result = await Swal.fire({
      title: 'Change Data Group',
      text: `The selected data group has fewer columns, so the values entered for ${columnNames} will be lost. Do you want to continue?`,
      showCancelButton: true,
      confirmButtonColor: '#0069be',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    });

    return !!result.value;
  }

  private clearHiddenPercentValues(): void {
    const visibleFields = this.percentColumns.map((percentColumn: EfDataPercentColumn) => percentColumn.field);
    const allFields = Object.values(EFateConstants.NODE_KEY_TO_PERCENT_FIELD);

    this.experimentRowData.forEach((experimentRow: EfDataExperimentRow) => {
      allFields
        .filter((field: string) => !visibleFields.includes(field))
        .forEach((field: string) => {
          experimentRow[field] = null;
        });
    });
  }

  private getExperimentRows(row: EfDataSetRow): EfDataExperimentRow[] {
    row.experimentData = row.experimentData ?? [];

    const lastRow = row.experimentData[row.experimentData.length - 1];
    if (!lastRow || !this.isBlankExperimentRow(lastRow)) {
      row.experimentData.push(this.createBlankExperimentRow(row.dataGroupKey));
    }

    return row.experimentData;
  }

  private resolveRowGroupKey(row: EfDataSetRow): string {
    const availableGroupKeys = this.dataGroupOptions.map((group: EfCakeTopologyGroupDefinition) => group.groupKey);
    return availableGroupKeys.includes(row.dataGroupKey) ? row.dataGroupKey : this.defaultGroupKey;
  }

  private ensureTrailingBlankRow(): void {
    const lastRow = this.experimentRowData[this.experimentRowData.length - 1];
    if (lastRow && this.isBlankExperimentRow(lastRow)) {
      return;
    }

    this.appendBlankExperimentRow();
  }

  private appendBlankExperimentRow(): void {
    const blankRow = this.createBlankExperimentRow(this.expandedRow?.dataGroupKey || this.defaultGroupKey);
    this.experimentRowData.push(blankRow);
    this.experimentGridApi?.applyTransaction({ add: [blankRow] });
  }

  private removeExperimentRow(row: EfDataExperimentRow): void {
    const index = this.experimentRowData.indexOf(row);
    if (index < 0) {
      return;
    }

    this.experimentRowData.splice(index, 1);
    this.experimentGridApi?.applyTransaction({ remove: [row] });
    this.ensureTrailingBlankRow();

    if (this.expandedRow) {
      this.expandedRow.modified = true;
    }
  }

  private createBlankExperimentRow(groupKey: string): EfDataExperimentRow {
    const blankRow: EfDataExperimentRow = { dataGroupKey: groupKey, sortOrder: 0, timeDays: null };

    Object.values(EFateConstants.NODE_KEY_TO_PERCENT_FIELD).forEach((field: string) => {
      blankRow[field] = null;
    });

    return blankRow;
  }

  private isBlankExperimentRow(row: EfDataExperimentRow): boolean {
    if (!row) {
      return true;
    }

    const fields = ['timeDays', ...Object.values(EFateConstants.NODE_KEY_TO_PERCENT_FIELD)];
    return fields.every((field: string) => this.isBlankValue(row[field]));
  }

  private isBlankValue(value: any): boolean {
    return value === null || value === undefined || `${value}`.trim() === '';
  }

  private buildDataGroupOptions(): EfCakeTopologyGroupDefinition[] {
    const groups = this.topologyState?.groups ?? [];

    if (groups.length === 0) {
      return [{
        groupKey: EFateConstants.DEFAULT_GROUP_KEY,
        groupName: `${EFateConstants.DEFAULT_GROUP_NAME_PREFIX} 1`,
        topologyKey: EFateConstants.DEFAULT_TOPOLOGY_KEY,
      }];
    }

    return groups;
  }

  private resolveDefaultGroupKey(): string {
    return this.dataGroupOptions[0]?.groupKey ?? EFateConstants.DEFAULT_GROUP_KEY;
  }

  getGroupColor(groupKey: string): string {
    const groupIndex = this.dataGroupOptions.findIndex((group: EfCakeTopologyGroupDefinition) => group.groupKey === groupKey);
    return EFateConstants.GROUP_COLORS[groupIndex >= 0 ? groupIndex : 0] ?? EFateConstants.GROUP_COLORS[0];
  }
}
